import React, { useContext, useState, useRef, useEffect } from "react";
import { Modal, Steps, Form, Upload, Button, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { NomenclatureForm } from "src/enitities/Form";
import { NomenclatureContext } from "src/shared/lib/hooks/context/getNomenclatureContext";
import { apiRequests } from "src/shared/api/api";

const { Step } = Steps;

export default function AddNomenclatureStepModal({ isOpen, setOpen }) {
  const { token, pathname } = useContext(NomenclatureContext) || {};
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [createdItem, setCreatedItem] = useState(null);
  const [pictures, setPictures] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmVisible, setIsConfirmVisible] = useState(false);
  const tabsRef = useRef(null);

  const resetState = () => {
    setCurrentStep(0);
    setCreatedItem(null);
    setPictures([]);
    form.resetFields();
  };

  const handleCancel = () => {
    const hasUnsavedChanges =
      (typeof form.isFieldsTouched === "function" && form.isFieldsTouched()) ||
      pictures.length > 0;

    if (hasUnsavedChanges) {
      setIsConfirmVisible(true);
      return;
    }

    resetState();
    setOpen(false);
  };

  const handleCreate = async () => {
    try {
      setIsSubmitting(true);
      // Валидируем обязательные поля перед созданием
      const values = await form.validateFields();
      const allValues = form.getFieldsValue(true);
      const payload = { ...allValues, ...values };

      if (!token || !pathname) {
        message.error("Не удалось определить контекст номенклатуры");
        return;
      }

      // Для номенклатуры backend ожидает список, поэтому отправляем [payload]
      const response = await apiRequests.nomenclature.post([payload]);

      const raw = response?.data ?? response;
      const arr = Array.isArray(raw) ? raw : (raw?.result ?? raw?.data ?? []);
      const data = Array.isArray(arr) ? arr[0] : arr;

      if (!data?.id) {
        message.warning(
          "Номенклатура создана, но сервер не вернул идентификатор. Загрузка фото может быть недоступна."
        );
      }

      setCreatedItem(data);
      setCurrentStep(1);
    } catch (error) {
      // Ошибки уже отображаются внутри API.crud.create / валидации формы
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFinish = () => {
    resetState();
    setOpen(false);
  };

  // Переключаем вкладки формы в зависимости от шага (Основное -> SEO -> Быстро -> Цены)
  useEffect(() => {
    if (!tabsRef.current || currentStep > 3) return;

    const stepToTabKey = ["main", "seo", "quick", "prices"];
    const targetKey = stepToTabKey[currentStep];

    if (tabsRef.current.setActiveTab && targetKey) {
      tabsRef.current.setActiveTab(targetKey);
    }
  }, [currentStep]);

  const renderStepContent = () => {
    // Шаги 0–3: работа с формой номенклатуры (Основное / SEO / Быстро / Цены)
    if (currentStep <= 3) {
      return (
        <NomenclatureForm
          ref={tabsRef}
          formContext={form}
          record={currentStep >= 1 ? createdItem : undefined}
          withoutImage={true}
          hideTabs={true}
          setChangeData={() => {}}
        />
      );
    }

    // Шаг 3: Фото
    if (!createdItem?.id) {
      return (
        <div>
          <p>
            Номенклатура создана, но идентификатор не получен. Загрузка
            изображений недоступна.
          </p>
        </div>
      );
    }

    return (
      <div>
        <p style={{ marginBottom: 16 }}>
          Загрузите фотографии для номенклатуры{" "}
          <strong>{createdItem.name || ""}</strong>.
        </p>
        <Upload
          action={`https://${process.env.REACT_APP_APP_URL}/api/v1/pictures/?token=${token}&entity=nomenclature&entity_id=${createdItem.id}`}
          fileList={pictures}
          listType="picture-card"
          onChange={({ fileList: newFileList, file: newFile }) => {
            if (newFile.status === "done") {
              setPictures(newFileList);
              message.success("Изображение было добавлено");
              // Уведомляем список номенклатуры, что фотографии обновились
              if (typeof window !== "undefined" && window.dispatchEvent) {
                window.dispatchEvent(
                  new CustomEvent("pictures-updated", {
                    detail: {
                      entity: "nomenclature",
                      entityId: createdItem.id,
                    },
                  })
                );
              }
              return;
            }
            setPictures(newFileList);
          }}
        >
          <div>
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>Загрузить</div>
          </div>
        </Upload>
      </div>
    );
  };

  const footerButtons = [];

  if (currentStep === 0) {
    // Шаг 1: создание номенклатуры
    footerButtons.push(
      <Button key="cancel" onClick={handleCancel}>
        Отмена
      </Button>
    );
    footerButtons.push(
      <Button
        key="next"
        type="primary"
        onClick={handleCreate}
        loading={isSubmitting}
      >
        Далее
      </Button>
    );
  } else if (currentStep > 0 && currentStep < 4) {
    // Шаги 2–4: просто переключаемся по шагам без дополнительных запросов
    footerButtons.push(
      <Button key="back" onClick={() => setCurrentStep((prev) => prev - 1)}>
        Назад
      </Button>
    );
    footerButtons.push(
      <Button
        key="next"
        type="primary"
        onClick={() => setCurrentStep((prev) => prev + 1)}
      >
        Далее
      </Button>
    );
  } else {
    // Последний шаг (Фото)
    footerButtons.push(
      <Button key="back" onClick={() => setCurrentStep((prev) => prev - 1)}>
        Назад
      </Button>
    );
    footerButtons.push(
      <Button key="done" type="primary" onClick={handleFinish}>
        Готово
      </Button>
    );
  }

  return (
    <>
      <Modal
        open={isOpen}
        onCancel={handleCancel}
        footer={footerButtons}
        width={1000}
        title="Добавить номенклатуру по шагам"
        destroyOnClose
      >
        <Steps current={currentStep} style={{ marginBottom: 24 }}>
          <Step title="Основное" />
          <Step title="SEO" />
          <Step title="Быстро" />
          <Step title="Цены" />
          <Step title="Фото" />
        </Steps>
        {renderStepContent()}
      </Modal>
      <Modal
        open={isConfirmVisible}
        title="Подтвердите действие"
        onOk={() => {
          setIsConfirmVisible(false);
          resetState();
          setOpen(false);
        }}
        onCancel={() => setIsConfirmVisible(false)}
        okText="Да"
        cancelText="Нет"
        zIndex={2000}
        getContainer={() => document.body}
      >
        <p>
          Вы действительно хотите закрыть окно? Несохранённые изменения будут потеряны.
        </p>
      </Modal>
    </>
  );
}
